import type { ReactElement } from "react"
import { wrapWithBackgroundIfNeeded, type BlockComponentProps } from "./shared"

export function TimelineBlock({ content, resolved }: BlockComponentProps) {
  const heading = (content?.heading as string) ?? "Our story"
  const items = (content?.items as Array<{
    year?: string
    title?: string
    description?: string
    image_url?: string
  }>) ?? []
  const layout = (content?.layout as "vertical" | "horizontal") ?? "vertical"
  const primaryColor = "var(--nos-color-primary, #3b82f6)"
  const tlSectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8"

  if (items.length === 0) {
    return (
      <section className={tlSectionClass} style={resolved.sectionStyle}>
        <h2
          className="text-2xl font-bold mb-6 text-center"
          style={{ color: primaryColor }}
        >
          {heading}
        </h2>
        <p className="text-center text-muted-foreground text-sm">
          No timeline items yet.
        </p>
      </section>
    )
  }

  if (layout === "horizontal") {
    return (
      <section className={tlSectionClass} style={resolved.sectionStyle} data-testid="timeline-section-horizontal">
        <h2
          className="text-2xl font-bold mb-6 text-center"
          style={{ color: primaryColor }}
        >
          {heading}
        </h2>
        <div className="overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 @sm:-mx-6 @md:-mx-8 px-4 @sm:px-6 @md:px-8">
          <div className="flex gap-6 min-w-max">
            {items.map((item, i) => (
              <div
                key={i}
                className="snap-center flex-shrink-0 w-72 max-w-[85vw] p-4 rounded-lg border bg-card"
                data-testid={`timeline-item-horizontal-${i}`}
              >
                {item.year && (
                  <span
                    className="text-sm font-semibold"
                    style={{ color: primaryColor }}
                  >
                    {item.year}
                  </span>
                )}
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title ? `Timeline image: ${item.title}` : "Timeline image"}
                    className="mt-2 w-full h-32 object-cover rounded-md"
                  />
                )}
                {item.title && (
                  <h3 className="font-semibold mt-2">{item.title}</h3>
                )}
                {item.description && (
                  <p className="text-muted-foreground text-sm mt-1 line-clamp-3">
                    {item.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return wrapWithBackgroundIfNeeded(
    content,
    <section className={tlSectionClass} style={resolved.sectionStyle} data-testid="timeline-section-vertical">
      <h2
        className="text-2xl font-bold mb-8 text-center"
        style={{ color: primaryColor }}
      >
        {heading}
      </h2>
      <div className="relative max-w-3xl mx-auto">
        {/* Vertical center line - desktop only */}
        <div
          className="hidden @md:block absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-px"
          style={{ backgroundColor: primaryColor, opacity: 0.5 }}
        />
        <div className="space-y-6">
          {items.map((item, i) => (
            <div
              key={i}
              className={`relative flex @md:items-center ${
                i % 2 === 0 ? "@md:flex-row" : "@md:flex-row-reverse"
              }`}
              data-testid={`timeline-item-vertical-${i}`}
            >
              {/* Mobile: left timeline line + dot */}
              <div
                className="@md:hidden absolute left-3 top-6 bottom-0 w-0.5 -translate-x-px"
                style={{ backgroundColor: primaryColor, opacity: 0.5 }}
              />
              <div
                className="hidden @md:flex absolute left-1/2 w-4 h-4 rounded-full -translate-x-1/2 z-10 flex-shrink-0"
                style={{ backgroundColor: primaryColor }}
              />
              <div
                className="@md:hidden absolute left-0 w-4 h-4 rounded-full z-10 flex-shrink-0 mt-1"
                style={{ backgroundColor: primaryColor }}
              />
              {/* Content card - alternating sides on desktop */}
              <div
                className={
                  i % 2 === 0
                    ? "@md:w-[calc(50%-2rem)] pl-10 @md:pl-0 @md:pr-8"
                    : "@md:w-[calc(50%-2rem)] pl-10 @md:pl-8 @md:text-right"
                }
              >
                <div className="rounded-lg border bg-card p-4">
                  {item.year && (
                    <span
                      className="text-sm font-semibold"
                      style={{ color: primaryColor }}
                    >
                      {item.year}
                    </span>
                  )}
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title ? `Timeline image: ${item.title}` : "Timeline image"}
                    className="mt-2 w-full max-w-xs rounded-md object-cover @md:max-w-full"
                  />
                )}
                  {item.title && (
                    <h3 className="font-semibold mt-2">{item.title}</h3>
                  )}
                  {item.description && (
                    <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
                  )}
                </div>
              </div>
              {/* Spacer for alignment on desktop */}
              <div className="hidden @md:block @md:w-[calc(50%-2rem)] @md:shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </section>
  ) as ReactElement
}
